import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import { isAthlete, isGuardian } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../utils/apiConfig";
import { fetchCompanyLogoBlobUrl } from "../utils/profilePictureUtils";
import {
    deleteProgramDocument,
    fetchProgramDocuments,
    getProgramDocumentDownloadUrl,
    ProgramDocumentDto,
} from "../services/programDocumentApi";
import {
    deleteProgramVideo,
    fetchProgramVideos,
    getProgramVideoPlaybackUrl,
    ProgramVideoDto,
} from "../services/programVideoApi";
import ProgramDocumentUploadModal from "./ProgramDocumentUploadModal";
import ProgramVideoModal from "./ProgramVideoModal";
import DeleteTaskModal from "./DeleteTaskModal";
import ResourceDisplayConfig from "./ResourceDisplayConfig";
import VideoLightbox from "./VideoLightbox";
import { fetchVisiblePolarisHubItems, PolarisHubItem } from "../services/polarisHubContentApi";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/program-page.css";
import "../styles/pagination.css";
import "../styles/athlete-admin-page-spacing.css";
import ProgramHeaderIcon from "../images/program/program-icon.png";
import polarisStarLogo from "../images/media-kit/polaris-star-logo.svg";
import EyeIcon from "../images/buttons/Eye.svg";
import EditIcon from "../images/buttons/Edit.svg";
import DeleteIcon from "../images/buttons/Delete.svg";
import { apiClient } from "../utils/apiClient";

function apiErrorMessage(e: unknown, fallback: string): string {
    const any = e as { message?: string; body?: { detail?: string; message?: string } };
    return any?.body?.detail || any?.body?.message || any?.message || fallback;
}

const ADMIN_PAGE_SIZE = 4;
const ATHLETE_PAGE_SIZE = 3;

type VideoLightboxState = { id: number; title: string; vimeoUrl: string };

export default function ProgramPage() {
    const { userInfo } = useAuth();
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const guardianViewingAthleteId = localStorage.getItem("guardianViewingAthleteId")?.trim() ?? null;
    const guardianForAthleteProfileId =
        isGuardian() && guardianViewingAthleteId
            ? Number.parseInt(guardianViewingAthleteId, 10)
            : undefined;
    const athleteView = isAthlete() || (isGuardian() && Boolean(guardianViewingAthleteId));
    const isAdminViewingAthlete = !athleteView && Boolean(athleteId);
    const navState = location.state as AthleteAdminNavState | null;
    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);
    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        isAdminViewingAthlete && Boolean(athleteId)
    );
    const pageSize = athleteView ? ATHLETE_PAGE_SIZE : ADMIN_PAGE_SIZE;
    const roles = userInfo?.roles?.length ? userInfo.roles : userInfo?.role ? [userInfo.role] : [];
    const isPolaris = roles.some((r) => r?.toUpperCase() === "POLARIS");
    const [polarisCompanyId, setPolarisCompanyId] = useState<string>("");

    const [videos, setVideos] = useState<ProgramVideoDto[]>([]);
    const [videoTotalElements, setVideoTotalElements] = useState(0);
    const [videoTotalPages, setVideoTotalPages] = useState(0);
    const [videoPage, setVideoPage] = useState(1);
    const [loadingVideos, setLoadingVideos] = useState(true);

    const [docs, setDocs] = useState<ProgramDocumentDto[]>([]);
    const [docTotalElements, setDocTotalElements] = useState(0);
    const [docTotalPages, setDocTotalPages] = useState(0);
    const [docPage, setDocPage] = useState(1);
    const [loadingDocs, setLoadingDocs] = useState(true);
    const [polarisVideos, setPolarisVideos] = useState<PolarisHubItem[]>([]);
    const [polarisResources, setPolarisResources] = useState<PolarisHubItem[]>([]);

    const [error, setError] = useState<string | null>(null);
    const [docModalOpen, setDocModalOpen] = useState(false);
    const [editDoc, setEditDoc] = useState<ProgramDocumentDto | null>(null);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [editVideo, setEditVideo] = useState<ProgramVideoDto | null>(null);

    const [deleteDocModalOpen, setDeleteDocModalOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState<ProgramDocumentDto | null>(null);
    const [deleteVideoModalOpen, setDeleteVideoModalOpen] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState<ProgramVideoDto | null>(null);

    const [videoLightbox, setVideoLightbox] = useState<VideoLightboxState | null>(null);

    const [companyLogoBlobUrl, setCompanyLogoBlobUrl] = useState<string | null>(null);
    const [companyLogoFailed, setCompanyLogoFailed] = useState(false);

    const [videoNameFilterInput, setVideoNameFilterInput] = useState("");
    const [docNameFilterInput, setDocNameFilterInput] = useState("");
    const [debouncedVideoNameFilter, setDebouncedVideoNameFilter] = useState("");
    const [debouncedDocNameFilter, setDebouncedDocNameFilter] = useState("");

    const [videosAccordionOpen, setVideosAccordionOpen] = useState(true);
    const [resourcesAccordionOpen, setResourcesAccordionOpen] = useState(true);

    /** After the first video/doc list request finishes, refetches do not use full-page loading (keeps filter inputs mounted + focused). */
    const videoListFetchStartedRef = useRef(false);
    const docListFetchStartedRef = useRef(false);

    const resolvedCompanyId: number | null = isPolaris
        ? (() => {
              const t = polarisCompanyId.trim();
              if (t === "") return null;
              const n = Number.parseInt(t, 10);
              return Number.isNaN(n) ? null : n;
          })()
        : null;

    const loadVideos = useCallback(async () => {
        const showInitialSpinner = !videoListFetchStartedRef.current;
        if (showInitialSpinner) {
            setLoadingVideos(true);
        }
        setError(null);
        try {
            if (isPolaris && resolvedCompanyId == null) {
                setVideos([]);
                setVideoTotalElements(0);
                setVideoTotalPages(0);
                return;
            }
            const page = await fetchProgramVideos(
                videoPage - 1,
                pageSize,
                resolvedCompanyId,
                athleteView ? undefined : debouncedVideoNameFilter,
                guardianForAthleteProfileId
            );
            setVideos(page.content);
            setVideoTotalElements(page.totalElements);
            setVideoTotalPages(page.totalPages);
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Failed to load program videos."));
        } finally {
            setLoadingVideos(false);
            videoListFetchStartedRef.current = true;
        }
    }, [
        videoPage,
        pageSize,
        isPolaris,
        resolvedCompanyId,
        athleteView,
        debouncedVideoNameFilter,
        guardianForAthleteProfileId,
    ]);

    const loadDocs = useCallback(async () => {
        const showInitialSpinner = !docListFetchStartedRef.current;
        if (showInitialSpinner) {
            setLoadingDocs(true);
        }
        setError(null);
        try {
            if (isPolaris && resolvedCompanyId == null) {
                setDocs([]);
                setDocTotalElements(0);
                setDocTotalPages(0);
                return;
            }
            const page = await fetchProgramDocuments(
                docPage - 1,
                pageSize,
                resolvedCompanyId,
                athleteView ? undefined : debouncedDocNameFilter,
                guardianForAthleteProfileId
            );
            setDocs(page.content);
            setDocTotalElements(page.totalElements);
            setDocTotalPages(page.totalPages);
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Failed to load program documents."));
        } finally {
            setLoadingDocs(false);
            docListFetchStartedRef.current = true;
        }
    }, [
        docPage,
        pageSize,
        isPolaris,
        resolvedCompanyId,
        athleteView,
        debouncedDocNameFilter,
        guardianForAthleteProfileId,
    ]);

    useEffect(() => {
        const t = setTimeout(() => {
            const next = videoNameFilterInput.trim();
            setDebouncedVideoNameFilter((prev) => {
                if (prev !== next) {
                    setVideoPage(1);
                }
                return next;
            });
        }, 300);
        return () => clearTimeout(t);
    }, [videoNameFilterInput]);

    useEffect(() => {
        const t = setTimeout(() => {
            const next = docNameFilterInput.trim();
            setDebouncedDocNameFilter((prev) => {
                if (prev !== next) {
                    setDocPage(1);
                }
                return next;
            });
        }, 300);
        return () => clearTimeout(t);
    }, [docNameFilterInput]);

    useEffect(() => {
        loadVideos();
    }, [loadVideos]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [videosFromPolaris, resourcesFromPolaris] = await Promise.all([
                    fetchVisiblePolarisHubItems("VIDEO"),
                    fetchVisiblePolarisHubItems("RESOURCE"),
                ]);
                if (!cancelled) {
                    setPolarisVideos(videosFromPolaris);
                    setPolarisResources(resourcesFromPolaris);
                }
            } catch {
                if (!cancelled) {
                    setPolarisVideos([]);
                    setPolarisResources([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!athleteView) {
            setCompanyLogoBlobUrl(null);
            setCompanyLogoFailed(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                let cacheKey = "";
                if (isGuardian() && guardianViewingAthleteId) {
                    const athleteRes = await apiClient(
                        `${API_BASE_URL}/api/athletes/id/${encodeURIComponent(guardianViewingAthleteId)}`,
                        {
                            method: "GET",
                            headers: { Accept: "application/json" },
                            credentials: "include",
                        }
                    );
                    if (!athleteRes.ok || cancelled) {
                        return;
                    }
                    const athleteJson: { email?: string } = await athleteRes.json();
                    cacheKey = athleteJson?.email ?? "";
                } else {
                    const profileRes = await apiClient(`${API_BASE_URL}/api/admin-profile`, {
                        method: "GET",
                        headers: { Accept: "application/json" },
                        credentials: "include",
                    });
                    if (!profileRes.ok || cancelled) {
                        return;
                    }
                    const profile: { email?: string; username?: string } = await profileRes.json();
                    cacheKey = profile?.email ?? profile?.username ?? "";
                }
                if (!cacheKey) {
                    return;
                }
                const logoBlob = await fetchCompanyLogoBlobUrl(cacheKey, false);
                if (!cancelled) {
                    setCompanyLogoBlobUrl(logoBlob);
                    setCompanyLogoFailed(false);
                }
            } catch {
                if (!cancelled) {
                    setCompanyLogoBlobUrl(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [athleteView, guardianViewingAthleteId]);

    const openAddDoc = () => {
        setEditDoc(null);
        setDocModalOpen(true);
    };

    const openEditDoc = (d: ProgramDocumentDto) => {
        setEditDoc(d);
        setDocModalOpen(true);
    };

    const openAddVideo = () => {
        setEditVideo(null);
        setVideoModalOpen(true);
    };

    const openEditVideo = (v: ProgramVideoDto) => {
        setEditVideo(v);
        setVideoModalOpen(true);
    };

    const handleViewDocument = async (id: number) => {
        try {
            const { url } = await getProgramDocumentDownloadUrl(id, guardianForAthleteProfileId);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Could not open document."));
        }
    };

    const handleViewVideoStaff = (v: ProgramVideoDto) => {
        if (!v.videoUrl) return;
        setVideoLightbox({ id: v.id, title: v.name, vimeoUrl: v.videoUrl });
    };

    const handleViewVideoAthlete = async (id: number, name: string) => {
        try {
            const { url } = await getProgramVideoPlaybackUrl(id, guardianForAthleteProfileId);
            setVideoLightbox({ id, title: name, vimeoUrl: url });
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Could not play video."));
        }
    };

    const handleViewPolarisVideo = (item: PolarisHubItem) => {
        setVideoLightbox({ id: item.id, title: item.title, vimeoUrl: item.url });
    };

    const handleViewPolarisResource = (item: PolarisHubItem) => {
        window.open(item.url, "_blank", "noopener,noreferrer");
    };

    const openDeleteDocConfirm = (d: ProgramDocumentDto) => {
        setDocToDelete(d);
        setDeleteDocModalOpen(true);
    };

    const openDeleteVideoConfirm = (v: ProgramVideoDto) => {
        setVideoToDelete(v);
        setDeleteVideoModalOpen(true);
    };

    const confirmDeleteProgramDocument = async () => {
        if (!docToDelete) return;
        try {
            await deleteProgramDocument(docToDelete.id);
            setDeleteDocModalOpen(false);
            setDocToDelete(null);
            await loadDocs();
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Delete failed."));
        }
    };

    const confirmDeleteProgramVideo = async () => {
        if (!videoToDelete) return;
        try {
            await deleteProgramVideo(videoToDelete.id);
            setDeleteVideoModalOpen(false);
            setVideoToDelete(null);
            await loadVideos();
        } catch (e: unknown) {
            setError(apiErrorMessage(e, "Delete failed."));
        }
    };

    const closeDeleteDocModal = () => {
        setDeleteDocModalOpen(false);
        setDocToDelete(null);
    };

    const closeDeleteVideoModal = () => {
        setDeleteVideoModalOpen(false);
        setVideoToDelete(null);
    };

    const videoStart = videoTotalElements === 0 ? 0 : (videoPage - 1) * pageSize + 1;
    const videoEnd = Math.min(videoPage * pageSize, videoTotalElements);
    const docStart = docTotalElements === 0 ? 0 : (docPage - 1) * pageSize + 1;
    const docEnd = Math.min(docPage * pageSize, docTotalElements);

    const addDisabled = isPolaris && resolvedCompanyId == null;

    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="Hub"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(
                null,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            )}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    const logoBlock = (
        <>
            {companyLogoBlobUrl && !companyLogoFailed ? (
                <img
                    src={companyLogoBlobUrl}
                    alt=""
                    className="program-card-logo"
                    onError={() => setCompanyLogoFailed(true)}
                />
            ) : (
                <div className="program-card-logo program-card-logo--placeholder" aria-hidden />
            )}
        </>
    );

    const polarisLogoBlock = (
        <img
            src={polarisStarLogo}
            alt="Polaris Athlete"
            className="program-card-logo program-card-logo--polaris"
            data-testid="polaris-hub-card-logo"
        />
    );

    const pageShellClass = isAdminViewingAthlete
        ? "program-page program-page--staff program-page-wrapper"
        : athleteView
          ? "program-page program-page--athlete"
          : "program-page program-page--staff page-container";

    const splitHubPanels = Boolean(isAdminViewingAthlete);

    const programIntro = !athleteView ? (
        <header className={`program-intro${splitHubPanels ? " program-intro--above-cards" : ""}`}>
            <div className="program-intro-row">
                <img src={ProgramHeaderIcon} alt="" className="program-intro-icon" width={56} height={56} />
                <p className="program-intro-text">
                    Share helpful resources you&apos;ve created within your community. Anything you add here will
                    be visible and accessible only to your student-athletes within your organization.
                </p>
            </div>
        </header>
    ) : null;

    return (
        <div className={pageShellClass}>
            {adminBreadcrumb}
            {splitHubPanels && programIntro}
            <div className={splitHubPanels ? "program-page-sections" : "program-page-inner"}>
                {isPolaris && (
                    <div className="program-polaris-bar" style={{ marginBottom: 16 }}>
                        <label htmlFor="polaris-company-id" style={{ marginRight: 8, fontWeight: 600 }}>
                            Company ID
                        </label>
                        <input
                            id="polaris-company-id"
                            type="text"
                            inputMode="numeric"
                            value={polarisCompanyId}
                            onChange={(e) => setPolarisCompanyId(e.target.value)}
                            placeholder="Organization company id"
                            style={{ padding: 8, borderRadius: 6, border: "1px solid #ccc", width: 200 }}
                        />
                    </div>
                )}
                {!splitHubPanels && programIntro}

                {error && (
                    <div className="program-error program-error--page" role="alert">
                        {error}
                    </div>
                )}

                {/* Hub videos */}
                <ProgramSectionShell split={splitHubPanels}>
                <section className="program-section" aria-label="Hub videos">
                    <div className="program-accordion-head">
                        <button
                            type="button"
                            className="program-accordion-toggle"
                            onClick={() => setVideosAccordionOpen((o) => !o)}
                            aria-expanded={videosAccordionOpen}
                            aria-controls="program-videos-panel"
                            id="program-videos-accordion-trigger"
                        >
                            <span className="program-section-title">
                                {athleteView ? "Hub Videos" : "My Hub Videos"}
                            </span>
                        </button>
                        <div className="program-section-head-right">
                            <span className="program-section-meta">
                                {videoTotalElements} {videoTotalElements === 1 ? "video" : "videos"}
                            </span>
                            {!athleteView && (
                                <button
                                    type="button"
                                    className="program-add-btn"
                                    onClick={openAddVideo}
                                    disabled={addDisabled}
                                >
                                    + Add Video
                                </button>
                            )}
                            {athleteView && (
                                <button
                                    type="button"
                                    className="program-accordion-chevron-btn"
                                    onClick={() => setVideosAccordionOpen((o) => !o)}
                                    aria-expanded={videosAccordionOpen}
                                    aria-controls="program-videos-panel"
                                    aria-label={
                                        videosAccordionOpen
                                            ? "Collapse hub videos section"
                                            : "Expand hub videos section"
                                    }
                                >
                                    <span className="program-accordion-chevron" aria-hidden>
                                        {videosAccordionOpen ? "▼" : "▶"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                    {athleteView ? (
                        videosAccordionOpen && (
                            <div
                                id="program-videos-panel"
                                role="region"
                                aria-labelledby="program-videos-accordion-trigger"
                                className="program-accordion-panel"
                            >
                                <p className="program-section-athlete-desc">
                                    These videos have been added by your organization and serve as quick links to helpful
                                    resources and important information.
                                </p>

                                {polarisVideos.length > 0 && (
                                    <div className="program-polaris-group" data-testid="polaris-hub-videos">
                                        <h3 className="program-polaris-heading">From Polaris Athlete</h3>
                                        <div className="program-card-grid">
                                            {polarisVideos.map((item) => (
                                                <div key={`polaris-video-${item.id}`} className="program-card">
                                                    <div className="program-card-body">
                                                        <div className="program-card-logo-wrap">{polarisLogoBlock}</div>
                                                        <div className="program-card-main">
                                                            <h3 className="program-card-title">{item.title}</h3>
                                                            {item.description && (
                                                                <p className="program-polaris-desc">{item.description}</p>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="program-card-view"
                                                                onClick={() => handleViewPolarisVideo(item)}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {loadingVideos && <div className="program-loading">Loading…</div>}

                                {!loadingVideos && videos.length === 0 && polarisVideos.length === 0 && (
                                    <p className="program-empty">No videos yet.</p>
                                )}

                                {!loadingVideos && videos.length > 0 && (
                                    <div className="program-card-grid">
                                        {videos.map((v) => (
                                            <div key={v.id} className="program-card">
                                                <div className="program-card-body">
                                                    <div className="program-card-logo-wrap">{logoBlock}</div>
                                                    <div className="program-card-main">
                                                        <h3 className="program-card-title">{v.name}</h3>
                                                        <button
                                                            type="button"
                                                            className="program-card-view"
                                                            onClick={() => handleViewVideoAthlete(v.id, v.name)}
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!loadingVideos && videoTotalPages >= 1 && (
                                    <div className="pagination" data-testid="pagination-videos">
                                        <div className="pagination-info">
                                            <span>
                                                Showing <strong>{videoStart}</strong>-<strong>{videoEnd}</strong> of{" "}
                                                <strong>{videoTotalElements}</strong> videos
                                            </span>
                                        </div>
                                        <div className="pagination-controls">
                                            <div className="page-nav">
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    onClick={() => setVideoPage((p) => Math.max(1, p - 1))}
                                                    disabled={videoPage <= 1}
                                                >
                                                    Previous
                                                </button>
                                                {Array.from({ length: videoTotalPages }, (_, i) => i + 1).map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        className={`page-number ${p === videoPage ? "page-number-active" : ""}`}
                                                        onClick={() => setVideoPage(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    onClick={() => setVideoPage((p) => Math.min(videoTotalPages, p + 1))}
                                                    disabled={videoPage >= videoTotalPages}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <div
                            id="program-videos-panel"
                            role="region"
                            aria-labelledby="program-videos-accordion-trigger"
                            className="program-accordion-panel"
                        >
                            {polarisVideos.length > 0 && (
                                <div className="program-polaris-group" data-testid="polaris-hub-videos">
                                    <h3 className="program-polaris-heading">From Polaris Athlete</h3>
                                    <div className="program-table-wrap">
                                        <table className="program-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Video name</th>
                                                    <th scope="col">Link</th>
                                                    <th className="program-table-actions" scope="col">
                                                        <span className="program-sr-only">View</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {polarisVideos.map((item) => (
                                                    <tr key={`polaris-video-${item.id}`}>
                                                        <td>{item.title}</td>
                                                        <td className="program-table-link-cell">
                                                            <span className="program-table-link-text" title={item.url}>
                                                                {item.url}
                                                            </span>
                                                        </td>
                                                        <td className="program-table-actions">
                                                            <div className="task-actions">
                                                                <button
                                                                    type="button"
                                                                    className="task-action-btn view-btn"
                                                                    title="Play video"
                                                                    onClick={() => handleViewPolarisVideo(item)}
                                                                >
                                                                    <img src={EyeIcon} alt="" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div className="program-table-wrap">
                                <table className="program-table" data-testid="program-org-videos-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Video name</th>
                                            <th scope="col">Link</th>
                                            <th className="program-table-actions program-table-accordion-header" scope="col">
                                                <div className="program-table-accordion-header-inner">
                                                    <span className="program-sr-only">Row actions</span>
                                                    <button
                                                        type="button"
                                                        className="program-accordion-chevron-btn program-accordion-chevron-btn--table"
                                                        onClick={() => setVideosAccordionOpen((o) => !o)}
                                                        aria-expanded={videosAccordionOpen}
                                                        aria-controls="program-videos-panel"
                                                        aria-label={
                                                            videosAccordionOpen
                                                                ? "Collapse hub videos section"
                                                                : "Expand hub videos section"
                                                        }
                                                    >
                                                        <span className="program-accordion-chevron" aria-hidden>
                                                            {videosAccordionOpen ? "▼" : "▶"}
                                                        </span>
                                                    </button>
                                                </div>
                                            </th>
                                        </tr>
                                        {videosAccordionOpen && (
                                            <tr className="program-table-filter-row">
                                                <th scope="col">
                                                    <input
                                                        id="program-video-name-filter"
                                                        type="search"
                                                        className="program-table-header-filter-input"
                                                        placeholder="Filter…"
                                                        value={videoNameFilterInput}
                                                        onChange={(e) => setVideoNameFilterInput(e.target.value)}
                                                        autoComplete="off"
                                                        aria-label="Filter by video name"
                                                    />
                                                </th>
                                                <th aria-hidden="true" />
                                                <th className="program-table-actions" aria-hidden="true" />
                                            </tr>
                                        )}
                                    </thead>
                                    {videosAccordionOpen &&
                                        (loadingVideos ? (
                                            <tbody>
                                                <tr>
                                                    <td colSpan={3} className="program-loading">
                                                        Loading…
                                                    </td>
                                                </tr>
                                            </tbody>
                                        ) : (
                                            <tbody>
                                                {videos.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="program-empty">
                                                            {debouncedVideoNameFilter
                                                                ? "No videos match this filter."
                                                                : 'No videos yet. Click "Add Video" to add a link.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    videos.map((v) => (
                                                        <tr key={v.id}>
                                                            <td>{v.name}</td>
                                                            <td className="program-table-link-cell">
                                                                <span
                                                                    className="program-table-link-text"
                                                                    title={v.videoUrl ?? ""}
                                                                >
                                                                    {v.videoUrl ?? "—"}
                                                                </span>
                                                            </td>
                                                            <td className="program-table-actions">
                                                                <div className="task-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn view-btn"
                                                                        title="Play video"
                                                                        onClick={() => handleViewVideoStaff(v)}
                                                                    >
                                                                        <img src={EyeIcon} alt="" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn edit-btn"
                                                                        title="Edit video"
                                                                        onClick={() => openEditVideo(v)}
                                                                    >
                                                                        <img src={EditIcon} alt="" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn delete-btn"
                                                                        title="Delete video"
                                                                        onClick={() => openDeleteVideoConfirm(v)}
                                                                    >
                                                                        <img src={DeleteIcon} alt="" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        ))}
                                </table>
                            </div>
                            {videosAccordionOpen && !loadingVideos && videoTotalPages >= 1 && (
                                <div className="pagination" data-testid="pagination-videos">
                                    <div className="pagination-info">
                                        <span>
                                            Showing <strong>{videoStart}</strong>-<strong>{videoEnd}</strong> of{" "}
                                            <strong>{videoTotalElements}</strong> videos
                                        </span>
                                    </div>
                                    <div className="pagination-controls">
                                        <div className="page-nav">
                                            <button
                                                type="button"
                                                className="page-btn"
                                                onClick={() => setVideoPage((p) => Math.max(1, p - 1))}
                                                disabled={videoPage <= 1}
                                            >
                                                Previous
                                            </button>
                                            {Array.from({ length: videoTotalPages }, (_, i) => i + 1).map((p) => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    className={`page-number ${p === videoPage ? "page-number-active" : ""}`}
                                                    onClick={() => setVideoPage(p)}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="page-btn"
                                                onClick={() => setVideoPage((p) => Math.min(videoTotalPages, p + 1))}
                                                disabled={videoPage >= videoTotalPages}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </section>
                </ProgramSectionShell>

                {/* Hub documents / resources */}
                <ProgramSectionShell split={splitHubPanels}>
                <section className="program-section" aria-label="Hub resources">
                    <div className="program-accordion-head">
                        <button
                            type="button"
                            className="program-accordion-toggle"
                            onClick={() => setResourcesAccordionOpen((o) => !o)}
                            aria-expanded={resourcesAccordionOpen}
                            aria-controls="program-resources-panel"
                            id="program-resources-accordion-trigger"
                        >
                            <span className="program-section-title">
                                {athleteView ? "Hub Resources" : "My Hub Resources"}
                            </span>
                        </button>
                        <div className="program-section-head-right">
                            <span className="program-section-meta">
                                {docTotalElements} {docTotalElements === 1 ? "resource" : "resources"}
                            </span>
                            {!athleteView && (
                                <button
                                    type="button"
                                    className="program-add-btn"
                                    onClick={openAddDoc}
                                    disabled={addDisabled}
                                >
                                    + Add Resources
                                </button>
                            )}
                            {athleteView && (
                                <button
                                    type="button"
                                    className="program-accordion-chevron-btn"
                                    onClick={() => setResourcesAccordionOpen((o) => !o)}
                                    aria-expanded={resourcesAccordionOpen}
                                    aria-controls="program-resources-panel"
                                    aria-label={
                                        resourcesAccordionOpen
                                            ? "Collapse hub resources section"
                                            : "Expand hub resources section"
                                    }
                                >
                                    <span className="program-accordion-chevron" aria-hidden>
                                        {resourcesAccordionOpen ? "▼" : "▶"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                    {athleteView ? (
                        resourcesAccordionOpen && (
                            <div
                                id="program-resources-panel"
                                role="region"
                                aria-labelledby="program-resources-accordion-trigger"
                                className="program-accordion-panel"
                            >
                                <p className="program-section-athlete-desc">
                                    These resources have been added by your organization and serve as quick links to helpful
                                    resources and important information.
                                </p>

                                {polarisResources.length > 0 && (
                                    <div className="program-polaris-group" data-testid="polaris-hub-resources">
                                        <h3 className="program-polaris-heading">From Polaris Athlete</h3>
                                        <div className="program-card-grid">
                                            {polarisResources.map((item) => (
                                                <div key={`polaris-resource-${item.id}`} className="program-card">
                                                    <div className="program-card-body">
                                                        <div className="program-card-logo-wrap">{polarisLogoBlock}</div>
                                                        <div className="program-card-main">
                                                            <h3 className="program-card-title">{item.title}</h3>
                                                            {item.description && (
                                                                <p className="program-polaris-desc">{item.description}</p>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="program-card-view"
                                                                onClick={() => handleViewPolarisResource(item)}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {loadingDocs && <div className="program-loading">Loading…</div>}

                                {!loadingDocs && docs.length === 0 && polarisResources.length === 0 && (
                                    <p className="program-empty">No resources uploaded yet.</p>
                                )}

                                {!loadingDocs && docs.length > 0 && (
                                    <div className="program-card-grid">
                                        {docs.map((d) => (
                                            <div key={d.id} className="program-card">
                                                <div className="program-card-body">
                                                    <div className="program-card-logo-wrap">{logoBlock}</div>
                                                    <div className="program-card-main">
                                                        <h3 className="program-card-title">{d.name}</h3>
                                                        <button
                                                            type="button"
                                                            className="program-card-view"
                                                            onClick={() => handleViewDocument(d.id)}
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!loadingDocs && docTotalPages >= 1 && (
                                    <div className="pagination" data-testid="pagination-resources">
                                        <div className="pagination-info">
                                            <span>
                                                Showing <strong>{docStart}</strong>-<strong>{docEnd}</strong> of{" "}
                                                <strong>{docTotalElements}</strong> resources
                                            </span>
                                        </div>
                                        <div className="pagination-controls">
                                            <div className="page-nav">
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    onClick={() => setDocPage((p) => Math.max(1, p - 1))}
                                                    disabled={docPage <= 1}
                                                >
                                                    Previous
                                                </button>
                                                {Array.from({ length: docTotalPages }, (_, i) => i + 1).map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        className={`page-number ${p === docPage ? "page-number-active" : ""}`}
                                                        onClick={() => setDocPage(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="page-btn"
                                                    onClick={() => setDocPage((p) => Math.min(docTotalPages, p + 1))}
                                                    disabled={docPage >= docTotalPages}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <div
                            id="program-resources-panel"
                            role="region"
                            aria-labelledby="program-resources-accordion-trigger"
                            className="program-accordion-panel"
                        >
                            {polarisResources.length > 0 && (
                                <div className="program-polaris-group" data-testid="polaris-hub-resources">
                                    <h3 className="program-polaris-heading">From Polaris Athlete</h3>
                                    <div className="program-table-wrap">
                                        <table className="program-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Resource name</th>
                                                    <th scope="col">Link</th>
                                                    <th className="program-table-actions" scope="col">
                                                        <span className="program-sr-only">View</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {polarisResources.map((item) => (
                                                    <tr key={`polaris-resource-${item.id}`}>
                                                        <td>{item.title}</td>
                                                        <td className="program-table-link-cell">
                                                            <span className="program-table-link-text" title={item.url}>
                                                                {item.url}
                                                            </span>
                                                        </td>
                                                        <td className="program-table-actions">
                                                            <div className="task-actions">
                                                                <button
                                                                    type="button"
                                                                    className="task-action-btn view-btn"
                                                                    title="Open resource"
                                                                    onClick={() => handleViewPolarisResource(item)}
                                                                >
                                                                    <img src={EyeIcon} alt="" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div className="program-table-wrap">
                                <table className="program-table" data-testid="program-org-resources-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Resource name</th>
                                            <th scope="col">Added</th>
                                            <th className="program-table-actions program-table-accordion-header" scope="col">
                                                <div className="program-table-accordion-header-inner">
                                                    <span className="program-sr-only">Row actions</span>
                                                    <button
                                                        type="button"
                                                        className="program-accordion-chevron-btn program-accordion-chevron-btn--table"
                                                        onClick={() => setResourcesAccordionOpen((o) => !o)}
                                                        aria-expanded={resourcesAccordionOpen}
                                                        aria-controls="program-resources-panel"
                                                        aria-label={
                                                            resourcesAccordionOpen
                                                                ? "Collapse hub resources section"
                                                                : "Expand hub resources section"
                                                        }
                                                    >
                                                        <span className="program-accordion-chevron" aria-hidden>
                                                            {resourcesAccordionOpen ? "▼" : "▶"}
                                                        </span>
                                                    </button>
                                                </div>
                                            </th>
                                        </tr>
                                        {resourcesAccordionOpen && (
                                            <tr className="program-table-filter-row">
                                                <th scope="col">
                                                    <input
                                                        id="program-resource-name-filter"
                                                        type="search"
                                                        className="program-table-header-filter-input"
                                                        placeholder="Filter…"
                                                        value={docNameFilterInput}
                                                        onChange={(e) => setDocNameFilterInput(e.target.value)}
                                                        autoComplete="off"
                                                        aria-label="Filter by resource name"
                                                    />
                                                </th>
                                                <th aria-hidden="true" />
                                                <th className="program-table-actions" aria-hidden="true" />
                                            </tr>
                                        )}
                                    </thead>
                                    {resourcesAccordionOpen &&
                                        (loadingDocs ? (
                                            <tbody>
                                                <tr>
                                                    <td colSpan={3} className="program-loading">
                                                        Loading…
                                                    </td>
                                                </tr>
                                            </tbody>
                                        ) : (
                                            <tbody>
                                                {docs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="program-empty">
                                                            {debouncedDocNameFilter
                                                                ? "No resources match this filter."
                                                                : 'No resources yet. Click "Add Resources" to upload.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    docs.map((d) => (
                                                        <tr key={d.id}>
                                                            <td>{d.name}</td>
                                                            <td>{formatDate(d.createdAt)}</td>
                                                            <td className="program-table-actions">
                                                                <div className="task-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn view-btn"
                                                                        title="View resource"
                                                                        onClick={() => handleViewDocument(d.id)}
                                                                    >
                                                                        <img src={EyeIcon} alt="" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn edit-btn"
                                                                        title="Edit resource"
                                                                        onClick={() => openEditDoc(d)}
                                                                    >
                                                                        <img src={EditIcon} alt="" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="task-action-btn delete-btn"
                                                                        title="Delete resource"
                                                                        onClick={() => openDeleteDocConfirm(d)}
                                                                    >
                                                                        <img src={DeleteIcon} alt="" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        ))}
                                </table>
                            </div>
                            {resourcesAccordionOpen && !loadingDocs && docTotalPages >= 1 && (
                                <div className="pagination" data-testid="pagination-resources">
                                    <div className="pagination-info">
                                        <span>
                                            Showing <strong>{docStart}</strong>-<strong>{docEnd}</strong> of{" "}
                                            <strong>{docTotalElements}</strong> resources
                                        </span>
                                    </div>
                                    <div className="pagination-controls">
                                        <div className="page-nav">
                                            <button
                                                type="button"
                                                className="page-btn"
                                                onClick={() => setDocPage((p) => Math.max(1, p - 1))}
                                                disabled={docPage <= 1}
                                            >
                                                Previous
                                            </button>
                                            {Array.from({ length: docTotalPages }, (_, i) => i + 1).map((p) => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    className={`page-number ${p === docPage ? "page-number-active" : ""}`}
                                                    onClick={() => setDocPage(p)}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="page-btn"
                                                onClick={() => setDocPage((p) => Math.min(docTotalPages, p + 1))}
                                                disabled={docPage >= docTotalPages}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </section>
                </ProgramSectionShell>

            </div>

            {!athleteView && !isAdminViewingAthlete && !isPolaris && <ResourceDisplayConfig />}

            <ProgramDocumentUploadModal
                open={docModalOpen}
                onClose={() => setDocModalOpen(false)}
                onSuccess={loadDocs}
                companyId={resolvedCompanyId}
                editDocumentId={editDoc?.id ?? null}
                initialName={editDoc?.name ?? ""}
            />

            <ProgramVideoModal
                open={videoModalOpen}
                onClose={() => setVideoModalOpen(false)}
                onSuccess={loadVideos}
                companyId={resolvedCompanyId}
                editVideo={
                    editVideo && editVideo.videoUrl
                        ? { id: editVideo.id, name: editVideo.name, videoUrl: editVideo.videoUrl }
                        : null
                }
            />

            <DeleteTaskModal
                open={deleteDocModalOpen}
                onClose={closeDeleteDocModal}
                onConfirm={confirmDeleteProgramDocument}
                taskTitle={docToDelete?.name}
                itemLabel="Resource"
            />

            <DeleteTaskModal
                open={deleteVideoModalOpen}
                onClose={closeDeleteVideoModal}
                onConfirm={confirmDeleteProgramVideo}
                taskTitle={videoToDelete?.name}
                itemLabel="Video"
            />

            {videoLightbox && (
                <VideoLightbox
                    open
                    resource={{
                        id: videoLightbox.id,
                        title: videoLightbox.title,
                        vimeoUrl: videoLightbox.vimeoUrl,
                    }}
                    onClose={() => setVideoLightbox(null)}
                />
            )}
        </div>
    );
}

function ProgramSectionShell({
    split,
    children,
}: {
    split: boolean;
    children: React.ReactNode;
}) {
    if (split) {
        return <div className="program-section-panel">{children}</div>;
    }
    return <>{children}</>;
}

function formatDate(iso: string) {
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}
